import * as _ from "lodash";
import * as angular from "angular";
import { CampaignDataProvider } from "./campaign.dp";
import { CheckManager } from "../shared/check-manager";
import { ExportDataProvider } from "./export.dp";
import { FieldDataProvider } from "./field.dp";
import { Campaign } from "../models/campaign.model";
import { Effort } from "../models/effort.model";
import { Field } from "../models/field.model";
import { Filter } from "../models/filter.model";
import { Group } from "../models/group.model";
import { HouseOccupant } from "../models/house-occupant.model";
import { Organisation } from "../models/organisation.model";
import { HouseOccupantV2Service } from "../services/house-occupant-v2.service";
import { HouseOccupantService } from "../services/house-occupant.service";
import { ToastUtils } from "../helper/toast.helper";
import { columnsConstants } from "../utils/constants/columns.constants";
import { HouseOccupantColumn } from "../utils/interfaces";
import { SortState } from "../components/shared/sortable-header/sortable-header.directive";

export class HouseOccupantDataProvider {
    private readonly defaultColumns = [
        ...columnsConstants.houseOccupantDefaultColumns,
    ];
    public houseoccupants: HouseOccupant[] = [];

    public filters: Filter = new Filter();
    public searchPromise: any;
    public columnsPromise: any;
    public countPromise: any;
    public selectedGroup: Group;
    public effort: Effort;
    public scrollLoading = false;
    public loading = true;
    public progress: any = null;
    public localStorage: any;
    public activeCampaign: Campaign;
    public isLeaderView: boolean;
    public error = false;
    public columns: any[] | undefined = undefined;
    public count: number | undefined = undefined;
    public countError: boolean | undefined = undefined;
    public currentSort: SortState = { column: null, direction: "desc" };

    private $q: any;
    private $http: any;
    private modelsUtils: any;
    private toast: ToastUtils;

    public checkManager: CheckManager;
    public service: HouseOccupantService;
    public v2Service: HouseOccupantV2Service;
    public campaignDataProvider: CampaignDataProvider;
    public exportDataProvider: ExportDataProvider;
    public fieldDataProvider: FieldDataProvider;

    constructor(
        $q: any,
        $http: any,
        __app_type: string,
        HouseOccupantService: HouseOccupantService,
        HouseOccupantV2Service: HouseOccupantV2Service,
        CampaignDataProvider: CampaignDataProvider,
        ExportDataProvider: ExportDataProvider,
        FieldDataProvider: FieldDataProvider,
        ModelsUtils: any,
        ToastUtils: ToastUtils
    ) {
        this.$q = $q;
        this.$http = $http;
        this.modelsUtils = ModelsUtils;
        this.toast = ToastUtils;

        this.service = HouseOccupantService;
        this.v2Service = HouseOccupantV2Service;
        this.campaignDataProvider = CampaignDataProvider;
        this.exportDataProvider = ExportDataProvider;
        this.fieldDataProvider = FieldDataProvider;

        this.checkManager = new CheckManager(this.houseoccupants);
        this.activeCampaign = CampaignDataProvider.getActiveCampaign();
        this.isLeaderView = __app_type == "LEADER";
    }

    setColumns(): void {
        this.columns =
            this.localStorage && this.localStorage.houseOccupantColumns
                ? this.localStorage.houseOccupantColumns
                : this.defaultColumns;

        angular.forEach(
            this.defaultColumns,
            (defaultColumn: HouseOccupantColumn) => {
                let found = false;
                angular.forEach(this.columns, (column: any) => {
                    if (defaultColumn.id == column.id) {
                        found = true;
                    }
                });
                if (!found) {
                    this.columns.push(defaultColumn);
                }
            }
        );
    }

    setDynamicFieldNamesToColumnsHelper(fields: Field[]): void {
        if (!this.columns) this.setColumns();
        this.renameColumnValuesBasedOnDynamic(this.defaultColumns, fields);
        this.renameColumnValuesBasedOnDynamic(this.columns, fields);

        if (this.localStorage) {
            this.localStorage["houseOccupantColumns"] = this.columns;
            this.activeCampaign.saveLocalStorage();
        }
    }

    renameColumnValuesBasedOnDynamic(
        columns: HouseOccupantColumn[],
        fields: any
    ): void {
        let withHoKey: HouseOccupantColumn[] = columns.filter(
            (column: HouseOccupantColumn) => column.hokey
        );
        angular.forEach(withHoKey, (column: any) => {
            column.name =
                this.fieldDataProvider.matchDynamicFieldName(fields, column) ||
                column.name;
        });

        let customFieldColumns: HouseOccupantColumn[] = columns.filter(
            (column: HouseOccupantColumn) => column.type == "customfield"
        );
        angular.forEach(customFieldColumns, (column: any) => {
            angular.forEach(
                [
                    ...fields.nonpriority,
                    ...fields.priority,
                    ...fields.priorityDisabled,
                ],
                (field) => {
                    if (field.cfid == column.id) {
                        column.name = field.name;
                    }
                }
            );
        });
    }

    setDynamicFieldNamesToColumns(): Promise<any> {
        const deferred = this.$q.defer();
        this.fieldDataProvider.getAllFields().then((fields: Field[]) => {
            this.setDynamicFieldNamesToColumnsHelper(fields);
            deferred.resolve(this.columns);
        });
        return deferred.promise;
    }

    getColumns(): Promise<any> {
        if (this.columnsPromise) {
            return this.columnsPromise;
        } else {
            const deferred = this.$q.defer();
            if (this.isLeaderView) {
                this.campaignDataProvider
                    .getOrganisations()
                    .then((organisation: Organisation) => {
                        this.localStorage = organisation.getLocalStorage();
                        this.setColumns();
                        this.setDynamicFieldNamesToColumns().then(() => {
                            this.setColumns();
                            deferred.resolve(this.columns);
                        });
                    });
            } else {
                setTimeout(() => {
                    this.localStorage = this.activeCampaign.getLocalStorage();
                    this.setColumns();
                    this.setDynamicFieldNamesToColumns().then(() => {
                        this.setColumns();
                        deferred.resolve(this.columns);
                    });
                }, 50);
            }
            this.columnsPromise = deferred.promise;
            return this.columnsPromise;
        }
    }

    clearAll(): void {
        this.filters = new Filter();
        this.searchPromise = null;
        this.countPromise = null;
        this.houseoccupants.splice(0, this.houseoccupants.length);
        this.count = undefined;
    }

    setFilters(setFilters: Filter, group?: Group): void {
        this.filters = setFilters;
        this.filters.removeSelectedUsers();
        if (group) this.selectedGroup = group;
        else this.selectedGroup = null;
        this.resetItems();
    }

    addCustomFieldsToColumns(customFields: Field[]): void {
        let activeIds = [];
        angular.forEach(customFields, (field: Field) => {
            let found = false;
            angular.forEach(this.columns, (column: any) => {
                if (column.type === "customfield" && field.cfid === column.id) {
                    found = true;
                }
            });
            if (!found && this.columns)
                this.columns.push({
                    id: field.cfid,
                    name: field.cfname,
                    selected: true,
                    type: "customfield",
                    cftype: field.cftname,
                });

            activeIds.push(field.cfid);
        });

        //remove any custom fields that have been deleted
        for (let i = this.columns.length - 1; i >= 0; i--) {
            if (
                this.columns[i].type === "customfield" &&
                activeIds.indexOf(this.columns[i].id) == -1
            ) {
                this.columns.splice(i, 1);
            }
        }
    }

    setEffort(newEffort: Effort): void {
        if (newEffort !== this.effort) {
            this.effort = newEffort;
            this.resetItems();
        }
    }

    addElectionsToColumns(elections: any[]): void {
        let activeIds = [];
        angular.forEach(elections, (election: any) => {
            let found = false;
            angular.forEach(this.columns, (column: any) => {
                if (column.type === "election" && election.elid === column.id) {
                    found = true;
                }
            });
            if (!found && this.columns)
                this.columns.push({
                    id: election.elid,
                    name: election.elname,
                    selected: true,
                    type: "election",
                });
            activeIds.push(election.elid);
        });

        //remove any elections that have been deleted
        for (let i = this.columns.length - 1; i >= 0; i--) {
            if (
                this.columns[i].type === "election" &&
                activeIds.indexOf(this.columns[i].id) == -1
            ) {
                this.columns.splice(i, 1);
            }
        }
    }

    getAll(
        offset: number,
        scroll?: boolean,
        sort?: SortState
    ): Promise<HouseOccupant[]> {
        if (
            (this.effort ||
                (this.filters.exclude_unsubs &&
                    this.filters.exclude_unsubs.checked)) &&
            this.filters &&
            Object.keys(this.filters).length &&
            (!angular.isDefined(this.count) ||
                !this.count ||
                this.houseoccupants.length < this.count)
        ) {
            if (sort !== undefined) {
                this.currentSort = sort;
            }

            this.error = false;

            if (scroll) {
                this.scrollLoading = true;
            } else {
                this.loading = true;
            }

            const deferred = this.$q.defer();
            if (this.searchPromise) {
                this.searchPromise.resolve("user cancelled");
            }
            this.searchPromise = this.$q.defer();
            const myPromise = this.searchPromise;
            this.progress = deferred.promise;

            // Only clear our loading flag if our request is still the
            // current one. If a newer getAll has replaced searchPromise,
            // it owns the loading state and we must not clobber it.
            const clearLoading = (): void => {
                if (this.searchPromise !== myPromise) {
                    return;
                }
                if (scroll) this.scrollLoading = false;
                else this.loading = false;
            };

            let activeFilters = this.filters.nonEmptyValues();
            if (this.selectedGroup) {
                activeFilters["selected_group"] = this.selectedGroup.gid;
            }

            let last = null;
            if (offset) {
                if (activeFilters["efforts"]) {
                    last = {
                        offset: this.houseoccupants.length,
                    };
                } else {
                    last = {
                        hoid: this.houseoccupants.length,
                    };
                }
            }

            const sortParam: string | undefined =
                this.currentSort.column
                    ? `${this.currentSort.column}:${this.currentSort.direction}`
                    : undefined;

            this.$http({
                url: "/api/houseoccupant/all",
                method: "GET",
                params: {
                    filters: JSON.stringify(activeFilters),
                    effort: this.effort,
                    last: last,
                    sort: sortParam,
                },
                timeout: this.searchPromise.promise,
            }).then(
                (response: object) => {
                    const json = response["data"];
                    if (!last) {
                        this.houseoccupants.splice(
                            0,
                            this.houseoccupants.length
                        );
                    }
                    if (json.success) {
                        // Only fetch the count when we don't have one. Sort
                        // doesn't change the count and pagination doesn't
                        // either, so we only need it on initial load and
                        // after a filter/effort change (which clear count
                        // via setFilteredHouseOccupantsCount(undefined)).
                        if (
                            !scroll &&
                            !angular.isDefined(this.count)
                        ) {
                            this.getCount().catch((): void => {
                                // Suppress unhandled rejection on abort;
                                // real errors set countError inside getCount.
                            });
                        }

                        if (
                            !last &&
                            json.data.length &&
                            (json.data[0].customfields ||
                                json.data[0].elections)
                        ) {
                            this.addCustomFieldsToColumns(
                                json.data[0].customfields
                            );
                            this.addElectionsToColumns(json.data[0].elections);
                        }
                        for (let i = 0; i < json.data.length; i++) {
                            let houseoccupant = this.modelsUtils.houseOccupant(
                                json.data[i]
                            );
                            this.checkManager.checkNewItem(houseoccupant);
                            this.houseoccupants.push(houseoccupant);
                        }

                        deferred.resolve(this.houseoccupants);
                    } else {
                        deferred.reject();
                    }
                    clearLoading();
                },
                (error: any) => {
                    // status === -1 means the request was aborted (e.g. a
                    // new fetch cancelled this one). Don't surface that as
                    // a UI error.
                    if (error.status > -1) {
                        this.error = true;
                    }
                    clearLoading();
                    deferred.reject(error);
                }
            );
            return deferred.promise;
        }
        return this.$q.reject();
    }

    getCount(): Promise<any> {
        this.countError = false;
        const deferred = this.$q.defer();

        let activeFilters = this.filters.nonEmptyValues();
        if (this.selectedGroup) {
            activeFilters["selected_group"] = this.selectedGroup.gid;
        }

        this.$http({
            url: "/api/houseoccupant/filteredCount",
            method: "GET",
            params: {
                filters: JSON.stringify(activeFilters),
                effort: this.effort,
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                let json = response["data"];
                if (json.success) {
                    this.setFilteredHouseOccupantsCount(parseInt(json.data));
                    deferred.resolve(parseInt(json.data));
                }
            },
            (error: any) => {
                if (error.status > -1) {
                    this.countError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    integrationLinks(houseoccupant: HouseOccupant): Promise<any> {
        const deferred = this.$q.defer();

        this.v2Service.integrationLinks(houseoccupant.hoid).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    if (houseoccupant.honbid && this.activeCampaign.slug) {
                        if (!json.data) json.data = {};
                        json.data.nationbuilder = {
                            link:
                                "https://" +
                                this.activeCampaign.slug +
                                ".nationbuilder.com/admin/signups/" +
                                houseoccupant.honbid,
                            error: null,
                        };
                    }
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getFilteredHouseOccupantsCount(): number | undefined {
        return this.count;
    }

    setFilteredHouseOccupantsCount(count: number | undefined): void {
        // Keep undefined as undefined so callers can use it to signal
        // "count not loaded" — getAll's gate `!angular.isDefined(this.count)`
        // relies on that distinction to decide whether to refetch.
        this.count = count;
        this.checkManager.setTotalCount(count ?? 0);
    }

    setSort(sort: SortState): void {
        this.currentSort = sort;
    }

    resetItems(): void {
        this.setFilteredHouseOccupantsCount(undefined);
        this.getAll(0, false);
    }

    removeFromFilteredHouseOccupants(houseoccupant: HouseOccupant): void {
        for (let i = 0; i < this.houseoccupants.length; i++) {
            let currentHouseOccupant = this.houseoccupants[i];
            if (currentHouseOccupant.hoid == houseoccupant.hoid) {
                this.houseoccupants.splice(i, 1);
                this.setFilteredHouseOccupantsCount(--this.count);
            }
        }
    }

    addGroupToFilter(filter: Filter, group?: Group): object {
        let filterCopy = _.cloneDeep(filter);
        if (group) {
            filterCopy.group.values.push(group.gid);
            filterCopy.group.checked = true;
        }
        return filterCopy;
    }

    bulkDelete(anonymize: boolean): Promise<any> {
        const deferred = this.$q.defer();
        let filterCopy = this.addGroupToFilter(
            this.filters,
            this.selectedGroup
        );
        let bulkActionData = this.checkManager.bulkActionData(
            new Filter(filterCopy)
        );
        bulkActionData["anonymize"] = anonymize;

        this.service.bulk_delete(bulkActionData).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.checkManager.removeCheckedItems();
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    bulkEdit(fieldEdit: any): Promise<any> {
        const deferred = this.$q.defer();
        let filterCopy = this.addGroupToFilter(
            this.filters,
            this.selectedGroup
        );
        let bulkActionData = this.checkManager.bulkActionData(
            new Filter(filterCopy)
        );
        bulkActionData["fieldEdit"] = fieldEdit;

        let callBack = (response: object) => {
            const json = response["data"];
            if (json.success) {
                this.checkManager.removeCheckedItems(false);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        };

        let errorCallback = (error: object) => {
            deferred.reject(error);
        };

        if (fieldEdit.field.cfid) {
            this.service
                .bulk_edit_custom_field(bulkActionData)
                .then(callBack, errorCallback);
        } else {
            this.service
                .bulk_edit(bulkActionData)
                .then(callBack, errorCallback);
        }

        return deferred.promise;
    }

    delete(houseoccupant: HouseOccupant): Promise<HouseOccupant> {
        const deferred = this.$q.defer();
        this.service.delete(houseoccupant.hoid).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.removeFromFilteredHouseOccupants(houseoccupant);
                    deferred.resolve(houseoccupant);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    bulkExport(exportChoices: object[]): Promise<any> {
        const deferred = this.$q.defer();
        let filterCopy = this.addGroupToFilter(
            this.filters,
            this.selectedGroup
        );
        let exportData = this.checkManager.bulkActionData(
            new Filter(filterCopy)
        );
        exportData["data_type"] = "people";
        exportData["exportChoices"] = exportChoices;
        exportData["timezone"] =
            Intl.DateTimeFormat().resolvedOptions().timeZone;

        this.exportDataProvider.create(exportData).then(
            (json: any) => {
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getHouseOccupant(hid: string): Promise<HouseOccupant> {
        const deferred = this.$q.defer();
        this.service.info(hid).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(this.modelsUtils.houseOccupant(json.data));
            }
            deferred.reject();
        });
        return deferred.promise;
    }

    processSocketUpdate(socketData: any): void {
        if (socketData.updatingCustomField) {
            if (socketData.success) {
                this.toast.success(
                    `Successfully updated custom field '${socketData.name}'`
                );
            } else {
                this.toast.error(
                    `Failed updating custom field '${socketData.name}'`
                );
            }
        }
    }
}

angular
    .module("vcDataProvider")
    .service("HouseOccupantDataProvider", HouseOccupantDataProvider);
